import { Button, ButtonGroup, Switch } from "@clockwise/design-system";
import CheckIcon from "@material-ui/icons/Check";
import LinkIcon from "@material-ui/icons/Link";
import { AvailabilityType } from "@clockwise/schema";
import { formatDuration } from "@util/duration.util";
import { getSchedulingLinkPath, getSchedulingLinkUrl } from "@util/scheduling.util";
import React, { MouseEvent, useCallback, useMemo, useState } from "react";
import { AVAILABILITY_TEXT } from "./constants";
import { useResettingBoolean } from "./hooks/useResettingBoolean";
import classNames from "classnames";
import { TrackingEvents, useTracking } from "@util/analytics.util";

export type Link = {
  /**
   * Clockwise username used when generating links.
   */
  linkName: string;
  meetingSettings: {
    id: string | null;
    slug: string;
    name: string;
    isActive: boolean;
    timingSettings: {
      /**
       * Duration of scheduled meeting in minutes.
       */
      durationMinutes: number;
    };
    availabilityType: AvailabilityType;
  };
};

export type Props = {
  link: Link;
  onChangeActive: (active: boolean) => Promise<void>;
  onClickCard: (linkUrl: string) => void;
  onEditLink: (slug: string) => void;
};

export default function LinkAdminCard({
  link: {
    linkName,
    meetingSettings: {
      id,
      name,
      isActive,
      slug,
      timingSettings: { durationMinutes },
      availabilityType,
    },
  },
  onChangeActive,
  onClickCard,
  onEditLink,
}: Props) {
  const trackEvent = useTracking();
  const [updating, setUpdating] = useState(false);
  const [copied, setCopied] = useResettingBoolean(false);
  const linkUrl = useMemo(() => getSchedulingLinkUrl(linkName, slug), [linkName, slug]);
  const linkPath = useMemo(() => getSchedulingLinkPath(linkName, slug), [linkName, slug]);

  const onActiveChange = useCallback(
    async (checked: boolean) => {
      setUpdating(true);
      await onChangeActive(checked);
      setUpdating(false);
    },
    [onChangeActive],
  );

  // Don't trigger the settings navigation when clicking the switch
  const onToggleActive = useCallback((e: MouseEvent<HTMLButtonElement>) => e.stopPropagation(), []);
  const onCardClick = useCallback(() => onClickCard(linkPath), [onClickCard, linkPath]);
  const onEdit = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onEditLink(slug);
    },
    [onEditLink, slug],
  );
  const onCopyLink = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      // Copy the given link to the clipboard
      await navigator.clipboard.writeText(linkUrl);
      setCopied(true);
      trackEvent(TrackingEvents.LINKS.SIDEPANEL.COPY_LINK, {
        meetingSettingsId: id,
        surface: "dashboard",
      });
    },
    [linkUrl],
  );

  return (
    <div
      className={classNames(
        "cw-font-brand cw-box-border cw-flex cw-flex-col cw-bg-white cw-px-4 cw-py-3 cw-rounded-lg cw-shadow-md cw-border-solid cw-border-2 cw-border-gray-100 cw-gap-2 cw-w-full cw-h-[228px]",
        { "cw-cursor-pointer": isActive },
      )}
      onClick={isActive ? onCardClick : undefined}
      cw-id="dashboard-card"
      aria-label={name}
      tabIndex={0}
    >
      <div className="cw-flex cw-justify-between cw-items-center">
        <Switch
          checked={isActive}
          onChange={onActiveChange}
          onClick={onToggleActive}
          disabled={updating}
          label={
            <span
              className={classNames(
                "cw-transition-colors cw-duration-200",
                isActive ? "cw-text-green-darker" : "cw-text-slate-standard",
              )}
            >
              {isActive ? "Active" : "Inactive"}
            </span>
          }
          name="active"
          cw-id="dashboard-card-active-switch"
        />
      </div>
      <div className="cw-flex cw-flex-col cw-gap-2">
        <div
          className="cw-text-xl cw-font-bold cw-text-dusk-standard cw-leading-6 cw-line-clamp-2"
          cw-id="dashboard-card-meeting-title"
          title={name}
        >
          {name}
        </div>
        <div className="cw-text-slate-standard cw-leading-tight cw-font-body">
          {formatDuration(durationMinutes)}, {AVAILABILITY_TEXT[availabilityType]}
        </div>
      </div>
      <div className="cw-flex-1" />
      <ButtonGroup>
        <Button
          startIcon={copied ? CheckIcon : LinkIcon}
          sentiment="positive"
          disabled={!isActive}
          onClick={onCopyLink}
          cw-id="dashboard-card-copy-link-button"
        >
          {copied ? "Copied" : "Copy link"}
        </Button>
        <Button
          sentiment="positive"
          variant="text"
          onClick={onEdit}
          cw-id="dashboard-card-edit-link-button"
        >
          Edit link
        </Button>
      </ButtonGroup>
    </div>
  );
}
